/**
 * The studio config (D32, Sofie's Studio): the rig — its devices and their
 * connections, the MAPPINGS from a show style's abstract layers to those
 * devices (lookahead mode, `oneShot`, device options), and studio settings.
 * What an operator plays lives in the show style (`showStyle.ts`); a
 * production resolves the pair into the config it runs
 * (`resolveConfig.ts` → `productionConfig.ts`).
 *
 *   devices    devices[id]      { type, standbyPolicy, options }   (D8's standby policy)
 *   mappings   mappings[layer]  { device, lookahead, …, oneShot, options }: `layer` is a
 *                               show-style layer id; one studio may map the layers of several
 *                               show styles (the ids share one namespace, as in Sofie)
 *   settings   { minimumTakeSpanMs }
 *
 * A stable export: the API stores what this schema outputs — defaults
 * applied and every mapping's `oneShot` resolved from its device's driver
 * when unset (`withOneShot`). Additive only.
 */
import { z } from 'zod';
import {
  configIdSchema,
  DEFAULT_MINIMUM_TAKE_SPAN_MS,
  deviceConfigSchema,
  layerIdSchema,
  mappingProblems,
  mappingSchema,
  studioSettingsSchema,
  withOneShot,
} from './productionConfig.js';

const studioConfigObjectSchema = z
  .object({
    devices: z.record(configIdSchema, deviceConfigSchema).default({}),
    mappings: z.record(layerIdSchema, mappingSchema).default({}),
    settings: studioSettingsSchema.default({ minimumTakeSpanMs: DEFAULT_MINIMUM_TAKE_SPAN_MS }),
  })
  .strict();

/** The whole studio config: every mapping names an existing device; `oneShot` resolved. */
export const studioConfigSchema = studioConfigObjectSchema
  .superRefine((config, ctx) => {
    for (const problem of mappingProblems(config)) ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
  })
  .transform((config) => ({ ...config, mappings: withOneShot(config) }));

export type StudioConfig = z.output<typeof studioConfigSchema>;
/** What a caller may write: defaults not yet applied. */
export type StudioConfigInput = z.input<typeof studioConfigSchema>;

export type ParseStudioResult = { readonly ok: true; readonly config: StudioConfig } | { readonly ok: false; readonly error: string };

/** Parse and cross-check untrusted studio-config input. Never throws. */
export function parseStudioConfig(input: unknown): ParseStudioResult {
  const result = studioConfigSchema.safeParse(input);
  return result.success ? { ok: true, config: result.data } : { ok: false, error: z.prettifyError(result.error) };
}
